'use client';

import { c as _c } from "react/compiler-runtime";
import { AssetPath } from '@gnsx/genesys.js';
import { Eraser as EraserIcon, FolderOpen as FolderOpenIcon, Image as ImageIcon, Layers as LayersIcon, Loader2, Upload as UploadIcon, X as XIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { OptimizedScenarioImage } from './components/OptimizedScenarioImage';
import type { IAssetFile } from '@/editor/store/models';
import type { ReferenceImage, ScenarioAsset } from '@/lib/scenario/scenarioTypes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { editorAssets, editorStore } from '@/editor/store/editorStore';
import { scenarioClient, scenarioService } from '@/lib/scenario';
import { cn } from '@/lib/utils';
interface ReferenceImageSelectorProps {
  referenceImage: ReferenceImage | null;
  onReferenceImageChange: (referenceImage: ReferenceImage | null) => void;
  disabled?: boolean;
  firebaseUid?: string;
}
interface AssetLibraryModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetSelect: (asset: ScenarioAsset) => void;
  firebaseUid?: string;
}

/** Project image thumbnail component */
const ProjectImageThumbnail = t0 => {
  const $ = _c(8);
  const {
    assetFile
  } = t0;
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  let t1;
  let t2;
  if ($[0] !== assetFile.path) {
    t1 = () => {
      let isCancelled = false;
      const loadThumbnail = async () => {
        ;
        try {
          const thumbnailService = editorStore.thumbnailService;
          if (!thumbnailService) {
            setIsLoading(false);
            return;
          }
          const storageProvider = editorStore.project.makeScopedStorageProvider();
          const resolvedPath = await storageProvider.resolvePath(AssetPath.fromString(assetFile.path));
          const url = resolvedPath.getResolvedPath();
          if (!isCancelled) {
            setThumbnailUrl(url);
            setIsLoading(false);
          }
        } catch (t3) {
          const error = t3;
          console.error("Failed to load project thumbnail:", error);
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
      };
      loadThumbnail();
      return () => {
        isCancelled = true;
      };
    };
    t2 = [assetFile.path];
    $[0] = assetFile.path;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (isLoading) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="w-full h-full flex items-center justify-center bg-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (!thumbnailUrl) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="w-full h-full flex items-center justify-center bg-muted"><ImageIcon className="h-8 w-8 text-muted-foreground" /></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== assetFile.name || $[6] !== thumbnailUrl) {
    t3 = <OptimizedScenarioImage src={thumbnailUrl} alt={assetFile.name} aspectRatio="auto" className="w-full h-full" />;
    $[5] = assetFile.name;
    $[6] = thumbnailUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

// Persist tab selection across modal opens/closes and component re-mounts
let persistedActiveTab: 'images' | 'project' = 'project'; // Default to 'project' tab

// Cache assets to prevent re-fetching on every re-render
let assetCache: {
  images: {
    assets: ScenarioAsset[];
    timestamp: number;
  } | null;
  project: {
    assets: ScenarioAsset[];
    projectFiles: IAssetFile[];
    timestamp: number;
  } | null;
} = {
  images: null,
  project: null
};
const CACHE_DURATION = 60000; // 1 minute cache

/**
 * Asset Library Modal for selecting reference images
 */
const AssetLibraryModal = ({
  isOpen,
  onOpenChange,
  onAssetSelect,
  firebaseUid
}: AssetLibraryModalProps) => {
  const [activeTab, setActiveTab] = useState<'images' | 'project'>(persistedActiveTab);
  const [assets, setAssets] = useState<ScenarioAsset[]>([]);
  const [projectAssetFiles, setProjectAssetFiles] = useState<IAssetFile[]>([]); // Store IAssetFile for project images
  const [isLoading, setIsLoading] = useState(false);

  // Update persisted tab when it changes
  const handleTabChange = useCallback((value: 'images' | 'project') => {
    persistedActiveTab = value;
    setActiveTab(value);
  }, []);
  const loadAssets = useCallback(async (assetType: 'images' | 'project', forceRefresh = false) => {
    // Check cache first
    const now = Date.now();
    const cached = assetCache[assetType];
    if (!forceRefresh && cached && now - cached.timestamp < CACHE_DURATION) {
      console.log(`💾 Using cached ${assetType} data`);
      setAssets(cached.assets);
      if (assetType === 'project' && 'projectFiles' in cached) {
        setProjectAssetFiles(cached.projectFiles);
      }
      return;
    }
    setIsLoading(true);
    try {
      if (assetType === 'project') {
        // Load project images from the local asset browser
        console.log('🔍 Loading project images...');
        console.log('📦 editorAssets available:', !!editorAssets);
        console.log('📦 assetTree available:', !!editorAssets?.assetTree);

        // Get all texture files from the asset tree
        const textureFiles = editorAssets.assetTree.textureFiles;
        console.log(`📦 Found ${textureFiles.length} texture files`);

        // Filter for image files only (png, jpg, jpeg, webp, etc.)
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.svg'];
        const imageFiles = textureFiles.filter(file => imageExtensions.some(ext => file.path.toLowerCase().endsWith(ext)));
        console.log(`🖼️ Filtered to ${imageFiles.length} image files`);
        if (imageFiles.length > 0) {
          console.log('Sample image paths:', imageFiles.slice(0, 3).map(f => f.path));
        }

        // Convert IAssetFile to ScenarioAsset format for compatibility
        const projectAssets: ScenarioAsset[] = imageFiles.map(file_0 => ({
          id: file_0.path,
          url: file_0.path,
          // The path is used as URL for project files
          mimeType: 'image/png',
          metadata: {
            type: 'project-image',
            seed: 0,
            numInferenceSteps: 0,
            kind: 'image',
            parentJobId: '',
            style: '',
            prompt: '',
            geometryEnforcement: 0,
            width: 0,
            height: 0,
            size: 0
          },
          properties: {
            width: 0,
            height: 0,
            size: 0
          },
          kind: 'image',
          source: 'project',
          ownerId: '',
          authorId: '',
          outputIndex: 0,
          description: '',
          automaticCaptioning: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          privacy: 'private',
          tags: ['type:project'],
          collectionIds: [],
          nsfw: [],
          status: 'completed',
          editCapabilities: []
        }));
        console.log(`✅ Reference Image Selector - Loaded ${projectAssets.length} project images`);

        // Update cache
        assetCache.project = {
          assets: projectAssets,
          projectFiles: imageFiles,
          timestamp: now
        };
        setAssets(projectAssets);
        setProjectAssetFiles(imageFiles); // Store the IAssetFile objects for thumbnail rendering
        return;
      }
      if (!firebaseUid) {
        console.error('❌ No Firebase UID provided for asset loading');
        setAssets([]);
        return;
      }

      // Load generated images only (no skyboxes)
      const response = await scenarioService.fetchUserAssets(firebaseUid, 'image');
      console.log(`🔍 Reference Image Selector - Loaded ${response.assets?.length ?? 0} ${assetType} assets for user`);
      const filteredAssets = response.assets || [];

      // Update cache
      assetCache.images = {
        assets: filteredAssets,
        timestamp: now
      };
      setAssets(filteredAssets);
    } catch (error) {
      console.error(`Failed to load ${assetType}:`, error);
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  }, [firebaseUid]);

  // Restore cached data immediately when component mounts or tab changes
  useEffect(() => {
    const cached_0 = assetCache[activeTab];
    if (cached_0 && Date.now() - cached_0.timestamp < CACHE_DURATION) {
      setAssets(cached_0.assets);
      if (activeTab === 'project' && 'projectFiles' in cached_0) {
        setProjectAssetFiles(cached_0.projectFiles);
      }
    }
  }, [activeTab]);

  // Load assets when modal opens or tab changes
  useEffect(() => {
    if (isOpen) {
      loadAssets(activeTab);
    }
  }, [isOpen, activeTab, loadAssets]);
  const handleAssetSelect = (asset: ScenarioAsset) => {
    onAssetSelect(asset);
    onOpenChange(false);
  };
  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <div>
            <DialogTitle>Select Reference Image</DialogTitle>
            <DialogDescription>
              Choose an image from your library to use as reference
            </DialogDescription>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={value_0 => handleTabChange(value_0 as 'images' | 'project')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="images">Generated</TabsTrigger>
            <TabsTrigger value="project">Project</TabsTrigger>
          </TabsList>

          <TabsContent value="images" className="mt-4">
            <div className="max-h-96 overflow-y-scroll text-sm">
              {isLoading ? <div className="text-muted-foreground text-center py-8">Loading images...</div> : assets.length === 0 ? <div className="text-center py-8 text-muted-foreground">
                  No images found in your library
                </div> : <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {assets.map(asset_0 => <div key={asset_0.id} className="rounded-lg overflow-hidden border cursor-pointer hover:border-primary transition-colors" onClick={() => handleAssetSelect(asset_0)}>
                      <OptimizedScenarioImage src={asset_0.url} alt="Asset" aspectRatio="square" />
                    </div>)}
                </div>}
            </div>
          </TabsContent>

          <TabsContent value="project" className="mt-4">
            <div className="max-h-96 overflow-y-scroll text-sm">
              {isLoading ? <div className="text-muted-foreground text-center py-8">Loading project images...</div> : assets.length === 0 ? <div className="text-center py-8 text-muted-foreground">
                  No images found in your project
                </div> : <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {assets.map((asset_1, index) => {
                const assetFile = projectAssetFiles[index];
                return <div key={asset_1.id} className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:border-primary transition-colors" onClick={() => handleAssetSelect(asset_1)}>
                        {assetFile ? <ProjectImageThumbnail assetFile={assetFile} /> : <div className="w-full h-full flex items-center justify-center bg-muted">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" />
                          </div>}
                      </div>;
              })}
                </div>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>;
};

/**
 * Reference Image Selector Component
 */
export const ReferenceImageSelector = ({
  referenceImage,
  onReferenceImageChange,
  disabled = false,
  firebaseUid
}: ReferenceImageSelectorProps) => {
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);

  // Handle file selection from input
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setIsUploading(true);
    try {
      const assetId = await scenarioService.uploadReferenceImage(file, file.name);

      // Create reference image object
      const newReferenceImage: ReferenceImage = {
        id: assetId,
        url: URL.createObjectURL(file),
        // Temporary preview URL
        source: 'upload',
        mode: 'img2img',
        // Default mode
        influence: 50 // Default influence
      };
      onReferenceImageChange(newReferenceImage);
    } catch (error) {
      console.error('Failed to upload reference image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [onReferenceImageChange]);

  // Handle asset selection from library
  const handleAssetSelect = useCallback(async (asset: ScenarioAsset) => {
    // Check if this is a project asset that needs to be uploaded first
    if (asset.tags?.includes('type:project')) {
      setIsUploading(true);
      try {
        console.log('🔄 Uploading project image:', asset.url);

        // Use the storage provider to get the actual file
        const storageProvider = editorStore.project.makeScopedStorageProvider();
        const resolvedPath = await storageProvider.resolvePath(AssetPath.fromString(asset.url));
        const actualUrl = resolvedPath.getResolvedPath();
        console.log('📁 Resolved path:', actualUrl);

        // Fetch the file from the resolved path
        const response = await fetch(actualUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const blob = await response.blob();
        const fileName = asset.url.split('/').pop() || 'project-image.png';
        const file_0 = new File([blob], fileName, {
          type: blob.type || 'image/png'
        });
        console.log('📤 Uploading to Scenario:', fileName, file_0.size, 'bytes');
        const assetId_0 = await scenarioService.uploadReferenceImage(file_0, fileName);
        console.log('✅ Upload complete, asset ID:', assetId_0);
        const newReferenceImage_0: ReferenceImage = {
          id: assetId_0,
          url: URL.createObjectURL(blob),
          source: 'library',
          mode: 'img2img',
          influence: 50
        };
        onReferenceImageChange(newReferenceImage_0);
      } catch (error_0) {
        console.error('❌ Failed to upload project image:', error_0);
        alert(`Failed to upload project image: ${error_0 instanceof Error ? error_0.message : 'Unknown error'}`);
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // Regular Scenario asset selection
    const newReferenceImage_1: ReferenceImage = {
      id: asset.id,
      url: asset.url,
      source: 'library',
      mode: 'img2img',
      // Default mode
      influence: 50 // Default influence
    };
    onReferenceImageChange(newReferenceImage_1);
  }, [onReferenceImageChange]);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  const handleDrop = useCallback((e_0: React.DragEvent) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    setDragActive(false);
    if (e_0.dataTransfer.files && e_0.dataTransfer.files[0]) {
      handleFileSelect(e_0.dataTransfer.files);
    }
  }, [handleFileSelect]);

  // Update reference image properties
  const updateReferenceImage = useCallback((updates: Partial<ReferenceImage>) => {
    if (referenceImage) {
      onReferenceImageChange({
        ...referenceImage,
        ...updates
      });
    }
  }, [referenceImage, onReferenceImageChange]);

  // Handle background removal
  const handleRemoveBackground = useCallback(async () => {
    if (!referenceImage || !firebaseUid) {
      return;
    }
    setIsRemovingBackground(true);
    try {
      // Track progress (optional for now)
      const updateProgress = (progress: number, status?: string) => {
        // Could add progress tracking here if needed
      };

      // Call the service method which handles the API call and polling
      const result = await scenarioService.removeBackground(referenceImage.id, updateProgress, firebaseUid);
      if (result.status === 'success' && result.images && result.images.length > 0) {
        // Get the new asset with background removed
        const newAssetId = result.images[0];
        const newAsset = await scenarioClient.getAsset(newAssetId);

        // Update the reference image with the new background-removed version
        const updatedReferenceImage: ReferenceImage = {
          ...referenceImage,
          id: newAsset.id,
          url: newAsset.url
        };
        onReferenceImageChange(updatedReferenceImage);
      } else {
        throw new Error('Background removal failed');
      }
    } catch (error_1) {
      console.error('❌ Background removal failed:', error_1);
      alert(`Background removal failed: ${error_1}`);
    } finally {
      setIsRemovingBackground(false);
    }
  }, [referenceImage, firebaseUid, onReferenceImageChange]);
  if (referenceImage) {
    // Compact view with image and inline controls
    return <div className="space-y-2">
        {/* Image preview with overlay controls */}
        <div className="relative group flex justify-center">
          <div className="relative rounded-lg overflow-hidden bg-muted/50 border border-border/50 inline-block">
            <OptimizedScenarioImage src={referenceImage.url} alt="Reference" className="max-w-full max-h-80 w-auto h-auto" aspectRatio="auto" objectFit="contain" />
            {/* Quick action buttons overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="icon" className="h-7 w-7 bg-black/60 hover:bg-black/80 border-none" onClick={handleRemoveBackground} disabled={disabled || isRemovingBackground}>
                    {isRemovingBackground ? <Loader2 className="h-3.5 w-3.5 text-white animate-spin" /> : <EraserIcon className="h-3.5 w-3.5 text-white" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Remove background</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="icon" className="h-7 w-7 bg-black/60 hover:bg-red-600/80 border-none" onClick={() => onReferenceImageChange(null)} disabled={disabled}>
                    <XIcon className="h-3.5 w-3.5 text-white" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Remove image</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Compact controls row */}
        <div className="flex items-center gap-2">
          {/* Mode toggle buttons */}
          <div className="flex rounded-md border border-border/50 overflow-hidden">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => updateReferenceImage({
                mode: 'img2img'
              })} disabled={disabled} className={cn('h-7 px-2 text-xs rounded-none border-r border-border/50', referenceImage.mode === 'img2img' && 'bg-primary/20 text-primary')}>
                  <ImageIcon className="h-3 w-3 mr-1" />
                  Style
                </Button>
              </TooltipTrigger>
              <TooltipContent>Colors and overall style</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => updateReferenceImage({
                mode: 'controlnet'
              })} disabled={disabled} className={cn('h-7 px-2 text-xs rounded-none', referenceImage.mode === 'controlnet' && 'bg-primary/20 text-primary')}>
                  <LayersIcon className="h-3 w-3 mr-1" />
                  Structure
                </Button>
              </TooltipTrigger>
              <TooltipContent>Structural blueprint</TooltipContent>
            </Tooltip>
          </div>

          {/* Influence slider - compact inline */}
          <div className="flex-1 flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Influence</Label>
            <Slider value={[referenceImage.influence]} onValueChange={([value]) => updateReferenceImage({
            influence: value
          })} min={0} max={100} step={1} className="flex-1 **:data-[slot=slider-track]:h-1 **:data-[slot=slider-thumb]:h-3 **:data-[slot=slider-thumb]:w-3" disabled={disabled} />
            <span className="text-xs text-muted-foreground w-8 text-right">{referenceImage.influence}%</span>
          </div>
        </div>
      </div>;
  }

  // Compact selection interface
  return <div className="space-y-2">
      {/* Drag & drop zone with integrated buttons */}
      <div className={cn('relative rounded-lg border border-dashed p-3 transition-all', dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-muted-foreground/50 bg-muted/30', disabled && 'opacity-50 cursor-not-allowed')} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
        {isUploading ? <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Uploading...</span>
          </div> : <div className="flex flex-col gap-2">
            {/* Drop hint - above buttons */}
            <div className="text-sm text-foreground/70 text-center font-medium">
              + Drag & drop image
            </div>

            {/* Buttons row */}
            <div className="flex items-center gap-2">
              {/* Library button */}
              <Button variant="ghost" size="sm" onClick={() => setIsLibraryModalOpen(true)} disabled={disabled} className="h-8 px-3 text-xs flex-1 justify-center gap-1.5 hover:bg-background/80">
                <FolderOpenIcon className="h-3.5 w-3.5" />
                Select from Library
              </Button>

              <div className="h-4 w-px bg-border" />

              {/* Upload button */}
              <Button variant="ghost" size="sm" onClick={() => {
            if (!disabled) {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = e_1 => {
                const target = e_1.target as HTMLInputElement;
                handleFileSelect(target.files);
              };
              input.click();
            }
          }} disabled={disabled} className="h-8 px-3 text-xs flex-1 justify-center gap-1.5 hover:bg-background/80">
                <UploadIcon className="h-3.5 w-3.5" />
                Upload
              </Button>
            </div>
          </div>}
      </div>

      {/* Asset Library Modal */}
      <AssetLibraryModal isOpen={isLibraryModalOpen} onOpenChange={setIsLibraryModalOpen} onAssetSelect={handleAssetSelect} firebaseUid={firebaseUid} />
    </div>;
};